import { assetsRoot } from "config";
import React, { FC, useContext, useState } from "react";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { useRouterWithRedirect } from "hooks/useRouterWithRedirect";
import { AppContext } from "context/app";
import { ModalContext } from "context/modal";
import { UserContext } from "context/user";
import { LoginForm as PepLoginForm } from "@pepdirect/v3/ui/LoginForm";
import {
  LoginMutationVariables,
  useLoginMutation,
} from "services/graphql/generated";
import { logEvent } from "@pepdirect/v3/helpers/analyticsLogger";
import { getLinkFromTenant } from "helpers/tenant";
import { parseLoginUserGraphQLErrors } from "@pepdirect/v3/helpers/errors";
import { EVENT_NAMES } from "@pepdirect/v3/types";

interface LoginFormProps {
  inModal?: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ inModal = false }) => {
  const { tenant } = useContext(AppContext);
  const { open, close } = useContext(ModalContext);
  const { setUser } = useContext(UserContext);
  const router = useRouter();

  const [mappedErrorMessage, setMappedErrorMessage] = useState("");
  const [showUpdatePasswordScreen, setShowUpdatePasswordScreen] =
    useState(false);

  const [loginMutation, { loading }] = useLoginMutation();
  const { pushPreserveRedirect, backToRedirect } = useRouterWithRedirect();

  const privacyLink = getLinkFromTenant(tenant, "privacy");

  const onSubmit = async ({ email, password }: LoginMutationVariables) => {
    setMappedErrorMessage("");
    try {
      const { data } = await loginMutation({
        variables: { email, password },
      });
      setUser(data?.loginUser?.id);
      logEvent(data?.loginUser?.id, EVENT_NAMES.userLoginSuccess);
      inModal ? close(data?.loginUser?.id) : backToRedirect();
    } catch (e) {
      if (e instanceof ApolloError && e.graphQLErrors.length) {
        const { errorMsg, passwordResetRequired } = parseLoginUserGraphQLErrors(
          e.graphQLErrors as GraphQLError[]
        );

        if (passwordResetRequired) {
          setShowUpdatePasswordScreen(true);
        } else {
          setMappedErrorMessage(errorMsg);
        }
      }
      return;
    }
  };

  const goToCreateAccount = () => {
    if (inModal) {
      close(null);
    }
    pushPreserveRedirect("/sign-up");
  };

  const goToForgotPassword = () => {
    if (inModal) {
      open("FORGOT_PASSWORD_MODAL");
    } else {
      close(null);
      pushPreserveRedirect("/forgot-password");
    }
  };

  const goToShop = () => {
    if (inModal) {
      close(null);
    } else {
      router.push("/");
    }
  };

  return (
    <PepLoginForm
      canSignUp
      customTexts={tenant?.brandConfig.customTexts}
      error={mappedErrorMessage || ""}
      goToCreateAccount={goToCreateAccount}
      goToForgotPassword={goToForgotPassword}
      goToShop={goToShop}
      inModal={inModal}
      isLoading={loading}
      logoSrc={`${assetsRoot}/website-assets/pepsi-drips-logo-dark.svg`}
      onSubmit={onSubmit}
      privacyUrl={privacyLink?.url}
      showUpdatePasswordScreen={showUpdatePasswordScreen}
      tenantName={tenant?.brandConfig.title}
    />
  );
};
